import { useSelect } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';

/**
 * Map the block's dynamic-source attributes to a core-data getUsers() query and
 * return the resolved users for the editor preview. Mirrors the server query in
 * AvatarGroup::query_source_users() so the editor matches the frontend.
 *
 * @param {string} source  One of manual|users|authors|customers|specific.
 * @param {Object} query   { roles, postType, number, order }.
 * @param {Array}  userIds Selected user IDs (specific mode).
 * @return {{users: Array, isResolving: boolean}} Users + loading state.
 */
export default function useDynamicUsers(source, query = {}, userIds = []) {
  return useSelect(
    (select) => {
      if (source === 'manual') {
        return { users: [], isResolving: false };
      }

      const number = Math.min(50, Math.max(1, query.number || 8));
      const order = query.order === 'desc' ? 'desc' : 'asc';
      const args = { per_page: number, orderby: 'name', order };

      if (source === 'users') {
        if (Array.isArray(query.roles) && query.roles.length) {
          args.roles = query.roles;
        }
      } else if (source === 'authors') {
        args.has_published_posts = [query.postType || 'post'];
      } else if (source === 'customers') {
        args.roles = ['customer'];
      } else if (source === 'specific') {
        if (!userIds.length) {
          return { users: [], isResolving: false };
        }
        args.include = userIds;
        args.orderby = 'include';
        args.per_page = userIds.length;
      }

      const core = select(coreStore);
      return {
        users: core.getUsers(args) || [],
        isResolving: core.isResolving('getUsers', [args]),
      };
    },
    [source, JSON.stringify(query), JSON.stringify(userIds)]
  );
}
